import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Check, Pencil, Undo2, X } from 'lucide-react';
import { api } from '../api';
import { useTheme } from '../theme';
import { Button, Icon, IconButton } from '../components/ui';
import { FASTIGHET_GRANSER_SLD, FASTIGHET_WMS_URL } from '../karta/mapGeometry';
import { boundaryFeatureCollection, fillToPolygons, rasterFor, wmsUrl, type Pt, type Raster } from '../karta/fastighetFill';

/**
 * EK1 · Lägg markgränsen ovanpå Lantmäteriets fastighetsgränser. Användaren trycker inuti sin
 * fastighet (en eller flera); gränslagret hämtas som bild från den fria WMS:en, ytan fylls fram
 * till linjerna och blir en polygon (karta/fastighetFill.ts). Polygonen kan justeras innan den sparas.
 */
type Status = { kind: 'idle' | 'busy' | 'ok' | 'error'; text: string };

/** Hämtar gränsbilden och läser pixlarna (WMS:en tillåter CORS). */
async function loadRaster(r: Raster): Promise<Uint8ClampedArray> {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = wmsUrl(r);
  await img.decode();
  const c = document.createElement('canvas');
  c.width = r.w; c.height = r.h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('canvas');
  ctx.drawImage(img, 0, 0);
  return ctx.getImageData(0, 0, r.w, r.h).data;
}

/**
 * Fyll från tryckpunkterna; når ytan bildens kant hämtas ett större utsnitt (högst tre försök).
 * Utsnittet beror bara på punkterna (inte på kartans zoom), så samma tryck ger samma yta.
 */
export async function computeBoundary(seeds: Pt[], load = loadRaster) {
  let span = 1500;
  for (let attempt = 0; attempt < 3; attempt++, span *= 2.5) {
    const r = rasterFor(seeds, span);
    const res = fillToPolygons(await load(r), r, seeds, 1);
    if (res.ok || res.reason !== 'edge') return res;
  }
  return { ok: false as const, reason: 'edge' as const };
}

const geodesicHa = (latlngs: L.LatLng[][]) =>
  latlngs.reduce((s, ring) => s + (L as any).GeometryUtil.geodesicArea(ring), 0) / 10_000;

export function MarkGransFranFastighet({ mark, onClose, onSaved }: {
  mark: { id: string; name: string; mapLat?: number | null; mapLng?: number | null; coordinatesJson?: string | null; areaHa?: number | null };
  onClose: () => void;
  onSaved: (m: { coordinatesJson?: string | null; areaHa?: number | null; mapLat?: number | null; mapLng?: number | null }) => void;
}) {
  const T = useTheme();
  const divRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const resultRef = useRef<L.FeatureGroup | null>(null);
  const seedsLayerRef = useRef<L.LayerGroup | null>(null);
  const [seeds, setSeeds] = useState<Pt[]>([]);
  const [polys, setPolys] = useState<Pt[][] | null>(null);
  const [areaHa, setAreaHa] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle', text: 'Tryck inuti din fastighet. Tryck i fler fastigheter för att slå ihop dem.' });
  const [saving, setSaving] = useState(false);
  const [updateArea, setUpdateArea] = useState(true);
  const runRef = useRef(0);

  // Kartan: OSM + Lantmäteriets gränser och beteckningar (samma lager som "Fastighetsgränser").
  useEffect(() => {
    if (!divRef.current) return;
    const center: [number, number] = mark.mapLat && mark.mapLng ? [mark.mapLat, mark.mapLng] : [59.322, 16.681];
    const map = L.map(divRef.current, { zoomControl: true }).setView(center, mark.mapLat ? 15 : 12);
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(map);
    (L as any).tileLayer.wms(FASTIGHET_WMS_URL, { layers: 'granser', format: 'image/png', transparent: true, attribution: '© Lantmäteriet', maxZoom: 19, styles: '', sld_body: FASTIGHET_GRANSER_SLD }).addTo(map);
    (L as any).tileLayer.wms(FASTIGHET_WMS_URL, { layers: 'text', format: 'image/png', transparent: true, maxZoom: 19 }).addTo(map);
    if (mark.coordinatesJson) {
      try {
        const old = L.geoJSON(JSON.parse(mark.coordinatesJson), { style: { color: '#0064FF', weight: 2, dashArray: '6,5', fillOpacity: 0 }, interactive: false }).addTo(map);
        if (old.getBounds().isValid()) map.fitBounds(old.getBounds(), { padding: [30, 30] });
      } catch { /* trasig gräns visas inte */ }
    }
    resultRef.current = L.featureGroup().addTo(map);
    seedsLayerRef.current = L.layerGroup().addTo(map);
    setTimeout(() => map.invalidateSize(), 50);
    return () => { map.remove(); mapRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const IDLE = 'Tryck inuti din fastighet. Tryck i fler fastigheter för att slå ihop dem.';
  const seedsRef = useRef<Pt[]>([]);
  // Räkna med nästa uppsättning punkter; den används bara om fyllningen lyckas.
  const recompute = (next: Pt[]) => {
    const map = mapRef.current;
    if (!map) return;
    if (!next.length) { seedsRef.current = []; setSeeds([]); setPolys(null); setAreaHa(null); setStatus({ kind: 'idle', text: IDLE }); return; }
    const run = ++runRef.current;
    setStatus({ kind: 'busy', text: 'Hämtar fastighetsgränserna…' });
    computeBoundary(next).then(res => {
      if (run !== runRef.current) return;
      if (!res.ok) {
        setStatus({ kind: 'error', text: res.reason === 'line' ? 'Du tryckte på en gränslinje – tryck en bit in i fastigheten.'
          : res.reason === 'edge' ? 'Hittade ingen sluten fastighet runt punkten (ytan verkar större än ca 20 km). Tryck i en annan fastighet.'
          : 'Ingen yta hittades.' });
        return;
      }
      seedsRef.current = next;
      setSeeds(next);
      setPolys(res.polygons);
      setAreaHa(res.areaHa);
      setStatus({ kind: 'ok', text: `${next.length === 1 ? '1 tryck' : `${next.length} tryck`} · ca ${res.areaHa.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} ha. Tryck i fler fastigheter, justera eller spara.` });
    }).catch(() => {
      if (run !== runRef.current) return;
      setStatus({ kind: 'error', text: 'Kunde inte hämta Lantmäteriets fastighetsgränser. Försök igen.' });
    });
  };

  // Tryck = ny punkt (inte medan polygonen justeras).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onClick = (e: L.LeafletMouseEvent) => { if (!editing) recompute([...seedsRef.current, [e.latlng.lng, e.latlng.lat]]); };
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tryckpunkterna på kartan.
  useEffect(() => {
    seedsLayerRef.current?.clearLayers();
    seeds.forEach(([lng, lat]) => L.circleMarker([lat, lng], { radius: 6, color: '#fff', weight: 2, fillColor: '#E8A33D', fillOpacity: 1, interactive: false }).addTo(seedsLayerRef.current!));
  }, [seeds]);

  // Visa resultatet.
  useEffect(() => {
    const g = resultRef.current;
    if (!g) return;
    g.clearLayers();
    (polys ?? []).forEach(ring => L.polygon(ring.map(([lng, lat]) => [lat, lng] as [number, number]), { color: '#E8A33D', weight: 3, fillOpacity: 0.18, interactive: false }).addTo(g));
  }, [polys]);

  const toggleEdit = () => {
    const g = resultRef.current;
    if (!g) return;
    const layers = g.getLayers() as any[];
    if (!editing) { layers.forEach(l => l.editing?.enable()); setEditing(true); return; }
    layers.forEach(l => l.editing?.disable());
    const rings = layers.map(l => (l.getLatLngs()[0] as L.LatLng[]));
    setPolys(rings.map(r => r.map(p => [p.lng, p.lat] as Pt)));
    const ha = geodesicHa(rings);
    setAreaHa(ha);
    setStatus({ kind: 'ok', text: `Justerad · ca ${ha.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} ha. Tryck i fler fastigheter (räknar om från Lantmäteriets linjer) eller spara.` });
    setEditing(false);
  };

  const currentRings = (): Pt[][] => {
    const layers = (resultRef.current?.getLayers() ?? []) as any[];
    return layers.map(l => (l.getLatLngs()[0] as L.LatLng[]).map(p => [p.lng, p.lat] as Pt));
  };

  const save = async () => {
    const rings = editing ? currentRings() : polys;
    if (!rings?.length) return;
    if (mark.coordinatesJson && !confirm('Ersätta markens nuvarande gräns? Delområden (såter) behålls.')) return;
    setSaving(true);
    try {
      const ha = editing ? geodesicHa(rings.map(r => r.map(([lng, lat]) => L.latLng(lat, lng)))) : areaHa;
      const saved = await api.setMarkFastighet(mark.id, {
        coordinatesJson: boundaryFeatureCollection(rings, mark.name, mark.coordinatesJson),
        areaHa: updateArea && ha ? ha : undefined,
      });
      onSaved(saved);
      onClose();
    } catch (e) {
      setStatus({ kind: 'error', text: e instanceof Error ? e.message : 'Kunde inte spara gränsen' });
    } finally { setSaving(false); }
  };

  const tone = editing ? T.text : status.kind === 'error' ? T.danger : status.kind === 'ok' ? T.text : T.textSub;
  return (
    <div role="dialog" aria-modal="true" aria-label="Markgräns från fastighetskartan" data-testid="fastighet-verktyg"
      style={{ position: 'fixed', inset: 0, zIndex: 10050, background: T.bg, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 'calc(8px + env(safe-area-inset-top, 0px)) 12px 8px 16px', borderBottom: `1px solid ${T.border}` }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Markgräns från fastighetskartan</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>Lantmäteriets fastighetsgränser · ungefärlig, kontrollera mot karta</div>
        </div>
        <IconButton label="Stäng" onClick={onClose}><Icon as={X} /></IconButton>
      </div>
      <div ref={divRef} style={{ flex: 1, minHeight: 0, cursor: editing ? 'default' : 'crosshair' }} data-testid="fastighet-karta" />
      <div style={{ padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))', borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div role="status" data-testid="fastighet-status" style={{ fontSize: 14, fontWeight: 600, color: tone }}>{editing ? 'Dra i hörnen för att justera. Tryck Klar när du är nöjd.' : status.text}</div>
        {polys && areaHa != null && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: T.text }}>
            <input type="checkbox" checked={updateArea} onChange={e => setUpdateArea(e.target.checked)} />
            Sätt markens areal till {areaHa.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} ha
          </label>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="secondary" size="sm" icon={<Icon as={Undo2} size={16} />} disabled={!seeds.length || editing || status.kind === 'busy'} onClick={() => recompute(seeds.slice(0, -1))}>Ångra</Button>
          <Button variant="secondary" size="sm" icon={<Icon as={editing ? Check : Pencil} size={16} />} disabled={!polys} onClick={toggleEdit} data-testid="fastighet-justera">{editing ? 'Klar' : 'Justera'}</Button>
          <Button size="sm" loading={saving} disabled={!polys || status.kind === 'busy'} onClick={save} data-testid="fastighet-spara" style={{ marginLeft: 'auto' }}>Spara som markgräns</Button>
        </div>
      </div>
    </div>
  );
}
